import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { DIALOG_TYPE } from '../hooks/useCriteriaManagement';

// Zorunlu diller: EN (1) ve TR (2)
const REQUIRED_LANGUAGE_IDS = [1, 2];

// EN ve TR'yi başa al, kalanını isme göre sırala
const sortLanguages = (languages) =>
    [...languages].sort((a, b) => {
        if (a.code === 'en') return -1;
        if (b.code === 'en') return 1;
        if (a.code === 'tr') return -1;
        if (b.code === 'tr') return 1;
        return a.name.localeCompare(b.name);
    });

const getFlagClassName = (lang) => `fi fi-${(lang.countryCode || lang.code || 'xx').toLowerCase()}`;

// Ağaç (GET /criteria) yalnızca seçili dildeki çözümlenmiş metni döner; `translations` dizisi gelmez.
// Bu yüzden düzenlemede mevcut dilin alanı, çözümlenmiş değerden (name/criterionText/evaluationText) doldurulur.
// PUT çevirileri upsert ettiği için yalnızca dolu diller gönderilir; diğer diller sunucuda korunur.
const buildInitialTranslations = (languages, type, data, currentLanguageId) => {
    const source = Array.isArray(data?.translations) ? data.translations : [];
    const hasSource = source.length > 0;
    return languages.map((lang) => {
        const existing = source.find((t) => t.languageId === lang.id);
        const seedCurrent = !hasSource && data && lang.id === currentLanguageId;
        if (type === DIALOG_TYPE.EVALUATION) {
            return {
                languageId: lang.id,
                criterionText: existing?.criterionText || (seedCurrent ? data?.criterionText || '' : ''),
                evaluationText: existing?.evaluationText || (seedCurrent ? data?.evaluationText || '' : '')
            };
        }
        return { languageId: lang.id, name: existing?.name || (seedCurrent ? data?.name || '' : '') };
    });
};

const CriteriaFormDialog = ({ visible, type, mode, data, parentId, onHide, onSubmit, isSaving, th }) => {
    const { getAvailableLanguages, getCurrentLanguageId } = useLanguage();
    const isEdit = mode === 'edit';
    const isEvaluation = type === DIALOG_TYPE.EVALUATION;
    const currentLanguageId = getCurrentLanguageId();

    const languages = useMemo(() => sortLanguages(getAvailableLanguages()), [getAvailableLanguages]);

    // Backend tüm çevirileri (translations dizisi) döndürdüğünde tüm diller dolu gelir → EN+TR zorunlu.
    // Henüz dönmüyorsa (yalnızca aktif dil seed edilir) sadece aktif dil zorunlu tutulur.
    const hasFullTranslations = Array.isArray(data?.translations) && data.translations.length > 0;
    const requireBaseLanguages = !isEdit || hasFullTranslations;
    const isLangRequired = (langId) =>
        requireBaseLanguages ? REQUIRED_LANGUAGE_IDS.includes(langId) : langId === currentLanguageId;

    const [sort, setSort] = useState(1);
    const [point, setPoint] = useState(0);
    const [isActive, setIsActive] = useState(true);
    const [translations, setTranslations] = useState(() => buildInitialTranslations(languages, type, null, currentLanguageId));
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setSubmitted(false);
        setSort(data?.sort ?? 1);
        setPoint(data?.point ?? 0);
        setIsActive(data?.isActive === undefined ? true : data.isActive === 1 || data.isActive === true);
        setTranslations(buildInitialTranslations(languages, type, data, currentLanguageId));
    }, [visible, type, data, languages, currentLanguageId]);

    const title = useMemo(() => {
        const map = {
            [DIALOG_TYPE.CRITERIA]: isEdit ? 'warrantyAudit.criteria.editTitle' : 'warrantyAudit.criteria.addTitle',
            [DIALOG_TYPE.SUB_CRITERIA]: isEdit ? 'warrantyAudit.subCriteria.editTitle' : 'warrantyAudit.subCriteria.addTitle',
            [DIALOG_TYPE.EVALUATION]: isEdit ? 'warrantyAudit.evaluation.editTitle' : 'warrantyAudit.evaluation.addTitle'
        };
        const fallback = {
            [DIALOG_TYPE.CRITERIA]: isEdit ? 'Kriter Düzenle' : 'Kriter Ekle',
            [DIALOG_TYPE.SUB_CRITERIA]: isEdit ? 'Alt Kriter Düzenle' : 'Alt Kriter Ekle',
            [DIALOG_TYPE.EVALUATION]: isEdit ? 'Değerlendirme Düzenle' : 'Değerlendirme Ekle'
        };
        return th(map[type]) || fallback[type];
    }, [type, isEdit, th]);

    const handleTextChange = (languageId, field, value) => {
        setTranslations((prev) => prev.map((t) => (t.languageId === languageId ? { ...t, [field]: value } : t)));
    };

    const isRequiredValid = useMemo(() => {
        const requiredIds = requireBaseLanguages ? REQUIRED_LANGUAGE_IDS : [currentLanguageId];
        return requiredIds.every((langId) => {
            const t = translations.find((x) => x.languageId === langId);
            if (isEvaluation) return (t?.criterionText || '').trim() && (t?.evaluationText || '').trim();
            return (t?.name || '').trim();
        });
    }, [translations, isEvaluation, requireBaseLanguages, currentLanguageId]);

    const handleSubmit = () => {
        setSubmitted(true);
        if (!isRequiredValid) return;

        // Boş çevirileri ele; en azından zorunlular dolu
        const cleanedTranslations = translations.filter((t) =>
            isEvaluation ? (t.criterionText || '').trim() || (t.evaluationText || '').trim() : (t.name || '').trim()
        );

        let payload = { sort };
        if (isEvaluation) payload.point = point;

        if (isEdit) {
            payload.id = data.id;
            payload.isActive = isActive ? 1 : 0;
        } else if (type === DIALOG_TYPE.SUB_CRITERIA) {
            payload.criteriaId = parentId;
        } else if (type === DIALOG_TYPE.EVALUATION) {
            payload.subCriteriaId = parentId;
        }

        payload.translations = cleanedTranslations;
        onSubmit(type, payload, mode);
    };

    const footer = (
        <div>
            <Button label={th('warrantyAudit.actions.cancel') || 'İptal'} icon="pi pi-times" onClick={onHide} outlined />
            <Button label={th('warrantyAudit.actions.save') || 'Kaydet'} icon="pi pi-check" onClick={handleSubmit} loading={isSaving} />
        </div>
    );

    const renderLangRow = (lang) => {
        const t = translations.find((x) => x.languageId === lang.id) || {};
        const required = isLangRequired(lang.id);
        const label = (
            <label>
                <span className={getFlagClassName(lang)} style={{ marginRight: '0.5em' }} title={lang.name} />
                {lang.name} {required && <span className="text-red-500">*</span>}
            </label>
        );

        if (isEvaluation) {
            const critInvalid = submitted && required && !(t.criterionText || '').trim();
            const evalInvalid = submitted && required && !(t.evaluationText || '').trim();
            return (
                <React.Fragment key={lang.id}>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            {label}
                            <InputText
                                value={t.criterionText || ''}
                                placeholder={th('warrantyAudit.evaluation.criterionText') || 'Değerlendirme Kriter'}
                                onChange={(e) => handleTextChange(lang.id, 'criterionText', e.target.value)}
                                className={critInvalid ? 'p-invalid' : ''}
                            />
                        </div>
                    </div>
                    <div className="col-12 md:col-6">
                        <div className="field">
                            <label>&nbsp;</label>
                            <InputText
                                value={t.evaluationText || ''}
                                placeholder={th('warrantyAudit.evaluation.evaluationText') || 'Değerlendirme'}
                                onChange={(e) => handleTextChange(lang.id, 'evaluationText', e.target.value)}
                                className={evalInvalid ? 'p-invalid' : ''}
                            />
                        </div>
                    </div>
                </React.Fragment>
            );
        }

        const invalid = submitted && required && !(t.name || '').trim();
        return (
            <div className="col-12 md:col-6" key={lang.id}>
                <div className="field">
                    {label}
                    <InputText
                        value={t.name || ''}
                        onChange={(e) => handleTextChange(lang.id, 'name', e.target.value)}
                        className={invalid ? 'p-invalid' : ''}
                    />
                </div>
            </div>
        );
    };

    return (
        <Dialog visible={visible} header={title} modal className="p-fluid" style={{ width: '900px' }} footer={footer} onHide={onHide}>
            <div className="grid p-inputtext-sm">
                <div className="col-6 md:col-3">
                    <div className="field">
                        <label htmlFor="sort">{th('warrantyAudit.fields.sort') || 'Sıralama'}</label>
                        <InputNumber id="sort" value={sort} onValueChange={(e) => setSort(e.value ?? 0)} min={0} inputClassName="p-inputtext-sm" />
                    </div>
                </div>

                {isEvaluation && (
                    <div className="col-6 md:col-3">
                        <div className="field">
                            <label htmlFor="point">{th('warrantyAudit.fields.point') || 'Puan'}</label>
                            <InputNumber id="point" value={point} onValueChange={(e) => setPoint(e.value ?? 0)} min={0} inputClassName="p-inputtext-sm" />
                        </div>
                    </div>
                )}

                {isEdit && (
                    <div className="col-12 md:col-3">
                        <div className="field-checkbox" style={{ marginTop: '2rem' }}>
                            <Checkbox inputId="isActive" checked={isActive} onChange={(e) => setIsActive(e.checked)} />
                            <label htmlFor="isActive" className="ml-2">
                                {th('warrantyAudit.fields.isActive') || 'Aktif'}
                            </label>
                        </div>
                    </div>
                )}

                <div className="col-12">
                    <hr />
                    <h5 className="mt-0">{th('warrantyAudit.fields.translations') || 'Çeviriler'}</h5>
                </div>

                {languages.map(renderLangRow)}

                {submitted && !isRequiredValid && (
                    <div className="col-12">
                        <small className="p-error">
                            {th('warrantyAudit.validation.requiredTranslations') || 'EN ve TR çevirileri zorunludur.'}
                        </small>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default CriteriaFormDialog;
